import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  AttachEvidenceFromLibraryInput,
  EvidenceFileDto,
  EvidenceType,
  LinkRequestInput,
  TargetRecordType,
} from './types';

export const fetchEvidenceList = async (
  batchId: string,
  filter?: { evidence_type?: EvidenceType; target_record_type?: TargetRecordType },
): Promise<EvidenceFileDto[]> => {
  const params: Record<string, string> = {};
  if (filter?.evidence_type) params.evidence_type = filter.evidence_type;
  if (filter?.target_record_type) params.target_record_type = filter.target_record_type;
  const res = await apiClient.get<ApiSuccess<EvidenceFileDto[]>>(
    `/monthly-batches/${batchId}/evidence-files`,
    { params },
  );
  return res.data.data;
};

export const uploadEvidence = async (
  batchId: string,
  file: File,
  evidence_type: EvidenceType,
): Promise<EvidenceFileDto> => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('evidence_type', evidence_type);
  const res = await apiClient.post<ApiSuccess<EvidenceFileDto>>(
    `/monthly-batches/${batchId}/evidence-files`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data;
};

export const attachEvidenceFromLibrary = async ({
  batchId,
  companyFileId,
  evidenceType,
}: AttachEvidenceFromLibraryInput): Promise<EvidenceFileDto> => {
  const res = await apiClient.post<ApiSuccess<EvidenceFileDto>>(
    `/monthly-batches/${batchId}/evidence-files/from-library`,
    {
      company_file_id: companyFileId,
      evidence_type: evidenceType,
    },
  );
  return res.data.data;
};

export const linkEvidence = async (
  evidenceFileId: string,
  body: LinkRequestInput,
): Promise<EvidenceFileDto> => {
  const res = await apiClient.post<ApiSuccess<EvidenceFileDto>>(
    `/evidence-files/${evidenceFileId}/link`,
    body,
  );
  return res.data.data;
};

export const evidenceDownloadUrl = (evidenceFileId: string): string => {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
  return `${base}/evidence-files/${evidenceFileId}/download`;
};

/**
 * Single source of truth for the file types evidence uploads accept: images plus
 * the video containers the download endpoint can stream inline — mp4 (Android
 * capture), mov / video/quicktime (iPhone capture) and webm. We list these
 * explicitly instead of a broad `video/*` so operators can only attach footage
 * that plays back in-browser; other containers (.avi, .mkv, …) never appear in
 * the picker. The backend stores any media up to MAX_UPLOAD_BYTES. Hardcoding
 * `accept="image/*"` per <input> is what let video support drift — some inputs
 * allowed video, others did not — so every evidence file input uses this.
 */
export const EVIDENCE_MEDIA_ACCEPT = 'image/*,video/mp4,video/quicktime,video/webm';
